import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Printer, Search, Sparkles, ArrowRight, Loader2 } from 'lucide-react';

import { AppRoutes } from '@/constants/routes';
import { cn } from '@/utils/cn';
import { sampleDataApi, type SampleScenario } from '@/api/sampleData.api';

/**
 * The guide to the sample data: what every seeded scenario is meant to demonstrate, what went into
 * it, and what the ledger should have done about it.
 *
 * A screen full of rows tells you nothing on its own. This is the other half — read it beside the
 * screen it describes, and the rows stop being data and start being examples.
 */
export const SampleDataGuidePage = () => {
  const [scenarios, setScenarios] = useState<SampleScenario[]>([]);
  const [loading, setLoading]     = useState(true);
  const [query, setQuery]         = useState('');
  const [area, setArea]           = useState<string>('all');

  useEffect(() => {
    sampleDataApi.catalogue()
      .then(setScenarios)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const areaKeys = useMemo(
    () => [...new Set(scenarios.map(s => s.area))],
    [scenarios]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scenarios.filter(s => {
      if (area !== 'all' && s.area !== area) return false;
      if (!q) return true;
      return [s.title, s.screen, s.whatItShows, s.expectedOutput, ...s.inputs]
        .some(t => t.toLowerCase().includes(q));
    });
  }, [scenarios, query, area]);

  // Group by screen, preserving the catalogue's own order — it runs down the ledger's dependency
  // spine, which is also the order in which the screens make sense to a newcomer.
  const byScreen = useMemo(() => {
    const groups = new Map<string, SampleScenario[]>();
    for (const s of filtered) {
      const list = groups.get(s.screen);
      if (list) list.push(s); else groups.set(s.screen, [s]);
    }
    return [...groups.entries()];
  }, [filtered]);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-auto print:bg-white">
      <div className="px-6 py-5 max-w-5xl w-full mx-auto space-y-5 print:max-w-none print:px-0">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2 print:text-black">
              <BookOpen size={20} className="text-signal print:hidden" /> Sample Data Guide
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 print:text-slate-700">
              Every situation the sample data demonstrates — what it shows, what goes in, and what the ledger does about it.
              Read it beside the screen it describes.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0 print:hidden">
            <button onClick={() => window.print()}
              className="px-3 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
              <Printer size={14} /> Print
            </button>
            <Link to={AppRoutes.ADMIN_SAMPLE_DATA}
              className="px-3 py-2 text-sm font-semibold text-white bg-signal hover:brightness-110 rounded-lg flex items-center gap-1.5">
              <Sparkles size={14} /> Generate
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search scenarios…"
              className="pl-8 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal/40 w-64" />
          </div>
          <button onClick={() => setArea('all')}
            className={cn('px-2.5 py-1.5 text-xs font-medium rounded-lg border',
              area === 'all'
                ? 'border-signal bg-signal/10 text-signal'
                : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800')}>
            All
          </button>
          {areaKeys.map(k => (
            <button key={k} onClick={() => setArea(k)}
              className={cn('px-2.5 py-1.5 text-xs font-medium rounded-lg border capitalize',
                area === k
                  ? 'border-signal bg-signal/10 text-signal'
                  : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800')}>
              {k}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-auto">
            {filtered.length} of {scenarios.length} scenario(s)
          </span>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading the catalogue…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-8 text-center text-sm text-slate-500">
            Nothing matches that search.
          </div>
        )}

        {/* The guide itself */}
        {byScreen.map(([screen, items]) => (
          <section key={screen} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden print:border-slate-300 print:break-inside-avoid">
            <header className="px-5 py-3 bg-slate-50 dark:bg-slate-700/40 border-b border-slate-200 dark:border-slate-700 print:bg-slate-100">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 print:text-black">{screen}</h2>
            </header>

            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {items.map(s => (
                <article key={s.key} className="px-5 py-4 print:break-inside-avoid">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white print:text-black">{s.title}</h3>
                    <span className="text-[10px] font-mono text-slate-400">{s.key}</span>
                  </div>

                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed print:text-slate-800">
                    {s.whatItShows}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-700/30 px-3 py-2.5 print:bg-slate-50 print:border print:border-slate-200">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">What goes in</div>
                      <ul className="space-y-0.5">
                        {s.inputs.map((i, n) => (
                          <li key={n} className="text-xs text-slate-700 dark:text-slate-300 flex gap-1.5">
                            <span className="text-slate-300 dark:text-slate-600">·</span>{i}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-lg bg-teal-50/60 dark:bg-teal-900/15 px-3 py-2.5 print:bg-teal-50 print:border print:border-teal-200">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-teal-600/80 dark:text-teal-400/80 mb-1 flex items-center gap-1">
                        <ArrowRight size={10} /> What the ledger does
                      </div>
                      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{s.expectedOutput}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};
